
import { useEffect } from 'react';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

interface ToastProps {
  message: string;
  type: ToastType;
  onClose: () => void;
  duration?: number;
}

export default function Toast({ message, type, onClose, duration = 3500 }: ToastProps) {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  const getToastStyles = () => {
    switch (type) {
      case 'success':
        return {
          bg: 'bg-gradient-to-r from-emerald-500 to-teal-500',
          icon: 'ri-checkbox-circle-fill',
          iconBg: 'bg-emerald-600'
        };
      case 'error':
        return {
          bg: 'bg-gradient-to-r from-red-500 to-rose-500',
          icon: 'ri-error-warning-fill',
          iconBg: 'bg-red-600'
        };
      case 'warning':
        return {
          bg: 'bg-gradient-to-r from-amber-500 to-orange-500',
          icon: 'ri-alert-fill',
          iconBg: 'bg-amber-600'
        };
      case 'info':
        return {
          bg: 'bg-gradient-to-r from-blue-500 to-indigo-500',
          icon: 'ri-information-fill',
          iconBg: 'bg-blue-600'
        };
      default:
        return {
          bg: 'bg-gradient-to-r from-gray-500 to-slate-500',
          icon: 'ri-information-fill',
          iconBg: 'bg-gray-600'
        };
    }
  };

  const styles = getToastStyles();

  return (
    <div className="animate-slide-in-right">
      <div className={`${styles.bg} rounded-xl shadow-2xl overflow-hidden min-w-[320px] max-w-md`}>
        <div className="flex items-start gap-3 p-4">
          <div className={`${styles.iconBg} rounded-lg p-2 flex items-center justify-center w-10 h-10 flex-shrink-0`}>
            <i className={`${styles.icon} text-white text-xl`}></i>
          </div>
          
          <div className="flex-1 pt-1">
            <p className="text-white font-medium text-sm leading-relaxed pr-2">
              {message}
            </p>
          </div>

          <button
            onClick={onClose}
            className="text-white/80 hover:text-white transition-colors flex-shrink-0 w-6 h-6 flex items-center justify-center"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <div className="h-1 bg-white/20">
          <div 
            className="h-full bg-white/40 animate-progress"
            style={{ animationDuration: `${duration}ms` }}
          ></div>
        </div>
      </div>
    </div>
  );
}
